"use client";
import { AnimatedGroup } from "@/components/ui/animated-group";
import { Button } from "@/components/ui/button";
import { TextEffect } from "@/components/ui/text-effect";
import { CalendarClock, Clock } from "lucide-react";
import Link from "next/link";

const transitionVariants = {
  item: {
    hidden: {
      opacity: 0,
      filter: "blur(12px)",
      y: 12,
    },
    visible: {
      opacity: 1,
      filter: "blur(0px)",
      y: 0,
      transition: {
        type: "spring" as const,
        bounce: 0.3,
        duration: 1.5,
      },
    },
  },
};

interface ComingSoonProps {
  title?: string;
  description?: string;
  ctaText?: string;
  ctaLink?: string;
  className?: string;
}

export default function ComingSoon({
  title = "Coming Soon",
  description = "We're working hard to bring you something amazing. Stay tuned for updates!",
  ctaText = "Notify Me",
  ctaLink = "#notify",
  className = "",
}: ComingSoonProps) {
  return (
    <section className={`relative overflow-hidden py-16 md:py-24 ${className}`}>
      {/* Background gradient effects */}
      <div
        aria-hidden
        className="absolute inset-0 isolate hidden opacity-65 contain-strict lg:block"
      >
        <div className="w-140 h-320 -translate-y-87.5 absolute left-0 top-0 -rotate-45 rounded-full bg-[radial-gradient(68.54%_68.72%_at_55.02%_31.46%,hsla(210,100%,85%,.08)_0,hsla(210,100%,55%,.02)_50%,hsla(210,100%,45%,0)_80%)]" />
        <div className="h-320 absolute left-0 top-0 w-60 -rotate-45 rounded-full bg-[radial-gradient(50%_50%_at_50%_50%,hsla(210,100%,85%,.06)_0,hsla(210,100%,45%,.02)_80%,transparent_100%)] [translate:5%_-50%]" />
      </div>

      <div className="mx-auto max-w-7xl px-6">
        <div className="text-center sm:mx-auto">
          <AnimatedGroup variants={transitionVariants}>
            <div className="bg-green-50 mx-auto flex w-fit items-center gap-3 rounded-full border border-green-200 p-1 pl-4 shadow-md dark:border-green-500/20 dark:bg-green-950/30 dark:shadow-green-950">
              <span className="text-green-700 dark:text-green-300 text-sm flex items-center gap-2">
                <CalendarClock className="size-4" />
                <span>Under Development</span>
              </span>
              <span className="dark:border-background block h-4 w-0.5 border-l bg-green-400 dark:bg-green-700"></span>
              <div className="bg-white dark:bg-green-900 size-6 overflow-hidden rounded-full">
                <div className="flex w-12">
                  <span className="flex size-6">
                    <Clock className="m-auto size-3 text-green-600 dark:text-green-300" />
                  </span>
                </div>
              </div>
            </div>
          </AnimatedGroup>

          <TextEffect
            preset="fade-in-blur"
            speedSegment={0.3}
            as="h2"
            className="mt-8 text-balance text-5xl text-green-500 dark:text-green-50 md:text-6xl lg:mt-12 font-semibold"
          >
            {title}
          </TextEffect>

          <TextEffect
            per="line"
            preset="fade-in-blur"
            speedSegment={0.3}
            delay={0.5}
            as="p"
            className="mx-auto mt-6 max-w-2xl text-balance text-lg text-green-800 dark:text-green-200 font-matter-medium"
          >
            {description}
          </TextEffect>

          <AnimatedGroup
            variants={{
              container: {
                visible: {
                  transition: {
                    staggerChildren: 0.05,
                    delayChildren: 0.75,
                  },
                },
              },
              ...transitionVariants,
            }}
            className="mt-10 flex flex-col items-center justify-center gap-2 md:flex-row"
          >
            <div
              key={1}
              className="bg-green-600/80 rounded-[calc(var(--radius-xl)+0.125rem)] border border-green-400 p-0.5"
            >
              <Button
                asChild
                size="lg"
                className="bg-green-600 hover:bg-green-700 text-white rounded-xl px-5 text-base"
              >
                <Link href={ctaLink}>
                  <span className="text-nowrap">{ctaText}</span>
                </Link>
              </Button>
            </div>
          </AnimatedGroup>

          {/* Decorative elements */}
          <div className="relative mx-auto mt-16 flex justify-center gap-8">
            <div className="size-20 rounded-full bg-green-100 opacity-30 animate-pulse dark:bg-green-900 dark:opacity-40"></div>
            <div
              className="size-14 rounded-full bg-green-200 opacity-20 animate-pulse dark:bg-green-800 dark:opacity-30"
              style={{ animationDelay: "300ms" }}
            ></div>
            <div
              className="size-8 rounded-full bg-green-300 opacity-10 animate-pulse dark:bg-green-700 dark:opacity-20"
              style={{ animationDelay: "600ms" }}
            ></div>
          </div>
        </div>
      </div>
    </section>
  );
}
